import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { apiClient } from "../../services/apiClient";
import "../../styles/line-clamp.css";

const DocumentSidebar = ({
  document,
  sections = [],
  selectedSection,
  onSectionSelect,
  proposal,
  hasUnsavedChanges,
  proposalDocuments = [],
  onBackToDocuments,
  onAnalyzeSections,
  onAddSection,
  onDeleteSection
}) => {
  const [exportLoading, setExportLoading] = React.useState(false);
  const getStatusBadge = (status) => {
    const statusConfig = {
      Draft: { bg: "bg-slate-100", text: "text-slate-700", icon: "edit" },
      In_Progress: { bg: "bg-blue-100", text: "text-blue-700", icon: "clock" },
      Review: { bg: "bg-amber-100", text: "text-amber-700", icon: "eye" },
      Final: { bg: "bg-emerald-100", text: "text-emerald-700", icon: "check" },
      Archived: { bg: "bg-slate-100", text: "text-slate-500", icon: "archive" },
    };

    const config = statusConfig[status] || statusConfig["Draft"];

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}
      >
        <FontAwesomeIcon icon={config.icon} className="mr-1" />
        {status}
      </span>
    );
  };

  const getDocumentTypeIcon = (documentType) => {
    const iconMap = {
      Technical_Proposal: "cogs",
      Past_Performance: "history",
      Key_Personnel: "users",
      Pricing_Proposal: "dollar-sign",
      Executive_Summary: "star",
      Corporate_Overview: "building",
      Custom: "file-alt",
    };

    return iconMap[documentType] || "file-alt";
  };

  const formatWordCount = (count) => {
    if (!count) return "0";
    return count.toLocaleString();
  };

  const getSectionProgress = (section) => {
    const wordCount = section.word_count || 0;
    if (wordCount === 0) return { status: "empty", color: "text-gray-400" };
    if (wordCount < 50) return { status: "minimal", color: "text-amber-600" };
    if (wordCount < 200) return { status: "developing", color: "text-blue-600" };
    return { status: "complete", color: "text-green-600" };
  };

  const handleViewDocument = (doc) => {
    apiClient.viewDocument(doc.document_id);
  };

  const handleExportDocument = async () => {
    try {
      setExportLoading(true);
      await apiClient.exportEditableDocument(document.document_id);
    } catch (error) {
      console.error('Failed to export document:', error);
      alert('Failed to export document. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return { icon: "file-pdf", color: "text-red-500" };
      case "doc":
      case "docx":
        return { icon: "file-word", color: "text-blue-500" };
      case "xls":
      case "xlsx":
        return { icon: "file-excel", color: "text-emerald-500" };
      default:
        return { icon: "file-alt", color: "text-slate-500" };
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Document Header */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-start space-x-3 mb-3">
          {/* Back Button */}
          {onBackToDocuments && (
            <button
              onClick={onBackToDocuments}
              className="flex-shrink-0 p-2 hover:bg-gray-200 rounded-lg transition-colors"
              title="Back to document list"
            >
              <FontAwesomeIcon
                icon="arrow-left"
                className="text-lg text-gray-600"
              />
            </button>
          )}

          <div className="flex-shrink-0">
            <FontAwesomeIcon
              icon={getDocumentTypeIcon(document.document_type)}
              className="text-2xl text-primary-500"
            />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {document.document_name}
            </h2>
            <p className="text-sm text-gray-600 truncate">
              {proposal?.proposal_name}
            </p>
          </div>
        </div>

        {/* RFP Documents Dropdown - full width below */}
        <select
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          onChange={(e) => {
            if (e.target.value) {
              const doc = proposalDocuments.find(d => d.document_id === parseInt(e.target.value));
              if (doc) handleViewDocument(doc);
              e.target.value = ""; // Reset selection
            }
          }}
          defaultValue=""
        >
          <option value="" disabled>
            View RFP Documents ({proposalDocuments.length})
          </option>
          {proposalDocuments.map((doc) => {
            const fileIcon = getFileIcon(doc.original_file_name);
            return (
              <option key={doc.document_id} value={doc.document_id}>
                {doc.original_file_name.replace(/_/g, " ")}
              </option>
            );
          })}
        </select>

        {/* Divider */}
        <div className="my-3 border-t border-gray-200"></div>

        {/* Export Button */}
        <button
          onClick={handleExportDocument}
          disabled={exportLoading}
          className="w-full px-3 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 text-white text-sm rounded transition-colors flex items-center justify-center space-x-2"
        >
          <FontAwesomeIcon icon={exportLoading ? "spinner" : "download"} className={exportLoading ? "animate-spin" : ""} />
          <span>{exportLoading ? "Exporting..." : "Download Full Document"}</span>
        </button>

        {/* Document Stats */}
        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-200">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">
              {sections.length}
            </div>
            <div className="text-xs text-gray-600">Sections</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">
              {formatWordCount(
                sections.reduce((sum, s) => sum + (s.word_count || 0), 0)
              )}
            </div>
            <div className="text-xs text-gray-600">Words</div>
          </div>
        </div>

        {/* Unsaved Changes Indicator */}
        {hasUnsavedChanges && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon="exclamation-triangle" className="text-amber-600" />
              <span className="text-sm text-amber-800">Unsaved changes</span>
            </div>
          </div>
        )}
      </div>

      {/* Sections List */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2">
          <div className="flex items-center justify-between px-2 py-2 mb-2">
            <h3 className="text-sm font-medium text-gray-700">Sections</h3>
            <span className="text-xs text-gray-500">{sections.length}</span>
          </div>

          {/* Section Action Buttons */}
          <div className="flex space-x-2 mb-3">
            <button
              onClick={onAnalyzeSections}
              className="flex-1 px-2 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs rounded transition-colors flex items-center justify-center space-x-1"
              title="Analyze document for missing sections"
            >
              <FontAwesomeIcon icon="search" className="text-xs" />
              <span>Analyze</span>
            </button>
            <button
              onClick={onAddSection}
              className="flex-1 px-2 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs rounded transition-colors flex items-center justify-center space-x-1"
              title="Add a custom section"
            >
              <FontAwesomeIcon icon="plus" className="text-xs" />
              <span>Add Section</span>
            </button>
          </div>

          {sections.length === 0 ? (
            <div className="text-center py-8">
              <FontAwesomeIcon
                icon="file-alt"
                className="text-2xl text-gray-300 mb-2"
              />
              <p className="text-sm text-gray-500">No sections yet</p>
            </div>
          ) : (
            <div className="space-y-1">
              {sections
                .sort((a, b) => a.section_order - b.section_order)
                .map((section, index) => {
                  const isSelected = selectedSection?.section_id === section.section_id;
                  const progress = getSectionProgress(section);

                  return (
                    <div
                      key={section.section_id}
                      onClick={() => onSectionSelect(section)}
                      className={`
                        group cursor-pointer rounded-lg p-2 transition-all duration-200
                        ${isSelected
                          ? "bg-primary-50 border-2 border-primary-200 shadow-sm"
                          : "bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                        }
                      `}
                    >
                      {/* Section Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-2 flex-1 min-w-0">
                          <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                            {index + 1}
                          </span>
                          <h4
                            className={`text-sm font-medium break-words flex-1 ${
                              isSelected ? "text-primary-700" : "text-gray-900"
                            }`}
                          >
                            {section.section_title}
                          </h4>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center space-x-1">
                          {/* Delete Button - Always visible */}
                          {onDeleteSection && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteSection(section);
                              }}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete section"
                            >
                              <FontAwesomeIcon icon="trash" className="text-xs" />
                            </button>
                          )}

                          {/* Progress Indicator */}
                          <div className={`text-xs ${progress.color} flex-shrink-0`}>
                            <FontAwesomeIcon
                              icon={
                                progress.status === "empty" ? "circle" :
                                progress.status === "minimal" ? "clock" :
                                progress.status === "developing" ? "edit" :
                                "check-circle"
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* Section Stats */}
                      <div className="flex items-center justify-between text-xs text-gray-500 mt-1 ml-8">
                        <span>{formatWordCount(section.word_count)} words</span>
                      </div>

                      {/* Selected Indicator */}
                      {isSelected && (
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-500 rounded-l-lg" />
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="text-xs text-gray-500 text-center">
          Last updated: {new Date(document.updated_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
};

export default DocumentSidebar;